import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CURRENT_EXHIBITIONS } from "@/data/content";
import Reveal from "@/components/site/Reveal";
import { useT, useScheduleTranslator, useRoute } from "@/i18n";

export default function CurrentlyOnView() {
  const t = useT();
  const tr = useScheduleTranslator();
  const route = useRoute();
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.85, 900);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="currently-on-view">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="eyebrow">{t.onView.eyebrow}</p>
            <h2 className="heading-display mt-3 text-5xl sm:text-6xl lg:text-7xl uppercase">
              {t.onView.title1} <span className="text-[#C20E1A]">{t.onView.title2}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="onview-prev"
              onClick={() => scrollBy(-1)}
              className="nav-arrow"
              aria-label="Previous"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="onview-next"
              onClick={() => scrollBy(1)}
              className="nav-arrow"
              aria-label="Next"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </Reveal>
      </div>

      <div
        ref={scrollerRef}
        className="hide-scrollbar overflow-x-auto scroll-smooth pl-6 md:pl-10 lg:pl-14"
      >
        <div className="flex gap-6 md:gap-8 pr-6 md:pr-10 lg:pr-14">
          {CURRENT_EXHIBITIONS.map((ex, i) => (
            <article
              key={`${ex.city}-${i}`}
              data-testid={`onview-card-${i}`}
              className="group shrink-0 w-[80vw] sm:w-[420px] lg:w-[460px] bg-white"
            >
              <div className="relative w-full aspect-[4/5] overflow-hidden bg-black/5">
                <img
                  src={ex.image}
                  alt={ex.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                />
                {ex.schedule && (
                  <div className="absolute top-4 left-4 bg-white/95 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold text-black">
                    {tr(ex.schedule)}
                  </div>
                )}
              </div>
              <div className="pt-6">
                {ex.touring && (
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#C20E1A] font-bold mb-2">
                    {t.common.touringExhibition}
                  </p>
                )}
                <h3 className="text-xl lg:text-2xl font-bold tracking-tight uppercase text-black">
                  {ex.title}
                </h3>
                <p className="mt-1 text-sm text-black/60">{ex.city}</p>
                <button
                  data-testid={`onview-visit-${i}`}
                  onClick={() => ex.link && window.open(ex.link, "_blank", "noopener,noreferrer")}
                  disabled={!ex.link}
                  className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-black hover:text-[#C20E1A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t.common.visitNow} <ArrowRight size={14} />
                </button>
              </div>
            </article>
          ))}
        </div>

        <Reveal className="mt-16 flex justify-center">
          <Link
            to={route("exhibitions")}
            data-testid="onview-discover-all"
            className="inline-flex items-center gap-3 bg-[#C20E1A] text-white px-10 py-5 text-[11px] uppercase tracking-[0.3em] font-bold hover:bg-black transition-colors duration-500"
          >
            {t.common.discoverAllExhibitions} <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
